import { useEffect, useState } from "react";
import {
  useDeleteProcuremetThreadsMutation,
  useGetProcuremetThreadsQuery,
} from "../../../store/apiSlices/procurementAI";
import { useSearchParams } from "react-router-dom";
import { TrashIcon } from "../../Icons";
import CustomModal from "../../DealTracker/CustomModal";
import { Thread } from "../../../types/ProcurementChat";

// helper fn
function stripHTML(html: string) {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

export default function ThreadSidebar() {
  const [threadsOpen, setThreadsOpen] = useState(true);
  const [isCreatingNewChat, setIsCreatingNewChat] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const activeThreadId = searchParams.get("threadId");
  const { data: threads, isLoading } = useGetProcuremetThreadsQuery();
  const [deleteProcuremetThreads, { isLoading: isDeleting }] =
    useDeleteProcuremetThreadsMutation();

  useEffect(() => {
    if (!activeThreadId && threads?.length && !isCreatingNewChat) {
      const firstThread = [...threads].reverse()[0]; // latest first (since you reverse in UI)

      setSearchParams({ threadId: firstThread.thread_id });
    }
  }, [threads, activeThreadId, isCreatingNewChat, setSearchParams]);

  const toggleModal = () => setIsModalOpen((prev) => !prev);

  const handleDelete = async (threadId: string | undefined) => {
    if (!threadId) return;

    const isActive = threadId === activeThreadId;

    await deleteProcuremetThreads({ thread_id: threadId }).unwrap();

    // if deleted thread was active → clear selection
    if (isActive) {
      setSearchParams({});
    }

    toggleModal();
  };

  return (
    <div
      className={`${
        threadsOpen ? "w-[220px]" : "w-[50px]"
      } border-r transition-all duration-300 flex flex-col`}
    >
      <div className="p-4 flex justify-between items-center border-b">
        {threadsOpen && <span className="font-medium">Threads</span>}
        <button
          onClick={() => setThreadsOpen(!threadsOpen)}
          className="text-blue-500"
        >
          {threadsOpen ? "<<" : ">>"}
        </button>
      </div>
      {isLoading && (
        <div className="p-2 space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="p-2 rounded animate-pulse bg-gray-100">
              <div className="h-3 w-[80%] bg-gray-300 rounded mb-2" />
              {/* <div className="h-3 w-[60%] bg-gray-200 rounded" /> */}
            </div>
          ))}
        </div>
      )}
      {threadsOpen && (
        <div className="relative p-2 pt-0 space-y-2 overflow-y-auto flex-1">
          {/* add new chat */}
          {!isLoading && (
            <div className="sticky top-0 z-10 bg-white border-b">
              <button
                className="w-full text-start p-2 my-1 text-sm rounded hover:bg-gray-100"
                onClick={() => {
                  setIsCreatingNewChat(true);
                  setSearchParams({});
                }}
              >
                + New chat
              </button>
            </div>
          )}

          {[...(threads || [])].reverse().map((thread) => {
            const rawText = thread.messages[0]?.user_chat || "Untitled thread";
            const cleanText = stripHTML(rawText);

            return (
              <div
                key={thread.thread_id}
                className={`group flex justify-between items-center p-2 text-sm rounded cursor-pointer ${
                  activeThreadId === thread.thread_id
                    ? "bg-gray-200"
                    : "hover:bg-gray-100"
                }`}
                onClick={() => setSearchParams({ threadId: thread.thread_id })}
              >
                <p className="truncate text-sm" title={cleanText}>
                  {cleanText ? cleanText : "Untitled thread"}
                </p>

                {/* Delete button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedThread(thread);
                    toggleModal();
                  }}
                  className={` transition-opacity p-1 bg-white rounded hover:text-red-500
                  ${
                    activeThreadId === thread.thread_id
                      ? "opacity-100"
                      : "opacity-0 group-hover:opacity-100"
                  }
                `}
                >
                  <TrashIcon className="size-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* delete modal */}
      <CustomModal
        open={isModalOpen}
        onClose={toggleModal}
        className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full"
      >
        <div className="flex flex-col gap-4">
          {/* Title */}
          <h2 className="text-lg font-semibold text-gray-900">Delete Thread</h2>

          {/* Context */}
          <div className="text-sm text-gray-600">
            Are you sure you want to delete this thread?
          </div>

          {/* Thread preview */}
          <div className="p-3 rounded-md bg-gray-50 border text-sm text-gray-700 flex items-start gap-2">
            {/* SVG Thread Icon */}
            <svg
              className="size-4 text-gray-400 mt-[2px] shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M7 7h10M7 12h7M7 17h4" />
              <circle cx="5" cy="7" r="1.5" />
              <circle cx="5" cy="12" r="1.5" />
              <circle cx="5" cy="17" r="1.5" />
            </svg>

            {/* Text */}
            <p
              className="truncate font-medium"
              dangerouslySetInnerHTML={{
                __html:
                  selectedThread?.messages?.[0]?.user_chat || "Untitled thread",
              }}
            ></p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={toggleModal}
              disabled={isDeleting}
              className={`px-4 py-2 text-sm rounded-md border bg-white transition ${
                isDeleting
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-gray-50"
              }`}
            >
              Cancel
            </button>

            <button
              onClick={() => handleDelete(selectedThread?.thread_id)}
              disabled={isDeleting}
              className={`px-4 py-2 text-sm rounded-md text-white transition ${
                isDeleting
                  ? "bg-red-300 cursor-not-allowed"
                  : "bg-red-500 hover:bg-red-600"
              }`}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
